import React, { useEffect, useState } from 'react';
import { MapPin, Clock, CheckCircle, Play, Navigation, LogOut, User, Truck, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useOSStore } from '../stores/osStore';
import { OrdemServico, FaseOS } from '../types';
import FaseCard from './FaseCard';


const Dashboard: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { 
    ordensServico, 
    ordemAtiva, 
    setOrdemAtiva, 
    fetchOrdensAtribuidas,
    isLoading,
    error 
  } = useOSStore();


  useEffect(() => {
    fetchOrdensAtribuidas();
  }, [fetchOrdensAtribuidas]);

  const handleLogout = () => {
    logout();
  };

  const getFasesAtribuidas = (): FaseOS[] => {
    if (!ordemAtiva || !user) return [];
    
    return ordemAtiva.fases.filter(fase => 
      fase.id_motorista_atribuido === user.id && 
      fase.status_fase !== 'Concluída'
    );
  };

  const getProximaFase = (): FaseOS | null => {
    const fasesAtribuidas = getFasesAtribuidas();
    return fasesAtribuidas.length > 0 ? fasesAtribuidas[0] : null;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendente': return 'text-yellow-600 bg-yellow-50';
      case 'Em Rota': return 'text-blue-600 bg-blue-50';
      case 'Concluída': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando suas fases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Nexus Motorista</h1>
                <p className="text-sm text-gray-500">Gerenciamento de Fases</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.nome}</p>
                <p className="text-xs text-gray-500">Motorista</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Sair"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Seleção de OS */}
        {ordensServico.length > 0 && (
          <div className="mb-6">
            <label htmlFor="os-select" className="block text-sm font-medium text-gray-700 mb-2">
              Ordem de Serviço Ativa
            </label>
            <select
              id="os-select"
              value={ordemAtiva?.id || ''}
              onChange={(e) => {
                const selected = ordensServico.find(os => os.id === parseInt(e.target.value));
                setOrdemAtiva(selected || null);
              }}
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {ordensServico.map((os) => (
                <option key={os.id} value={os.id}>
                  {os.numero_nota_contratacao ? `N.C. ${os.numero_nota_contratacao}` : `OS #${os.codigo_acesso}`} - {os.nome_falecido}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Status Geral */}
        {ordemAtiva && (
          <div className="mb-6 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {ordemAtiva.numero_nota_contratacao ? `N.C. ${ordemAtiva.numero_nota_contratacao}` : `Ordem de Serviço #${ordemAtiva.codigo_acesso}`}
              </h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ordemAtiva.status_geral)}`}>
                {ordemAtiva.status_geral}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Falecido</p>
                  <p className="text-sm text-gray-500">{ordemAtiva.nome_falecido}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Criada em</p>
                  <p className="text-sm text-gray-500">
                    {new Date(ordemAtiva.criado_em).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Fases Concluídas</p>
                  <p className="text-sm text-gray-500">
                    {ordemAtiva.fases.filter(f => f.status_fase === 'Concluída').length} / {ordemAtiva.fases.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        

        {/* Fases Atribuídas */}
        {ordemAtiva && getFasesAtribuidas().length > 0 ? (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Suas Fases Atribuídas
            </h3>
            {getFasesAtribuidas().map((fase) => (
              <FaseCard 
                key={fase.id} 
                fase={fase} 
                ordemServico={ordemAtiva}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Truck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma fase atribuída
            </h3>
            <p className="text-gray-500">
              {ordemAtiva 
                ? 'Você não tem fases atribuídas nesta OS.'
                : 'Selecione uma ordem de serviço para ver suas fases.'
              }
            </p>
          </div>
        )}

        {/* Sem OSs */}
        {ordensServico.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <Truck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma ordem de serviço atribuída
            </h3>
            <p className="text-gray-500">
              Aguarde o administrador atribuir fases a você.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
